import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useSync } from "@/context/SyncContext";
import { Logo } from "@/components/Logo";
import { Skeleton } from "@/components/ui/skeleton";
import { SiteFooter } from "@/components/SiteFooter";
import {
  ArrowLeft, CheckCircle2, Circle, Clock, AlertTriangle, UploadCloud,
  ChevronRight, Info, Lock, Camera, KeyRound, Send,
} from "lucide-react";

const SECTION_ICON = { "Evidence sync": UploadCloud, "Final documentation": Lock, "Return record": KeyRound, "Follow-through": Send };

const STATUS_STYLE = {
  done: { cls: "bg-[#DCFCE7] text-[#166534]", label: "Recorded", Icon: CheckCircle2 },
  synced: { cls: "bg-[#DCFCE7] text-[#166534]", label: "Synced", Icon: CheckCircle2 },
  in_progress: { cls: "bg-[#FEF3C7] text-[#92400E]", label: "In progress", Icon: Clock },
  available: { cls: "bg-[#E0E7FF] text-[#3730A3]", label: "Open", Icon: ChevronRight },
  not_started: { cls: "bg-[#F1F5F9] text-[#64748B]", label: "Not started", Icon: Circle },
  pending: { cls: "bg-[#FEF3C7] text-[#92400E]", label: "Queued", Icon: Clock },
  uploading: { cls: "bg-[#DBEAFE] text-[#1E40AF]", label: "Uploading", Icon: UploadCloud },
  failed: { cls: "bg-[#FEE2E2] text-[#991B1B]", label: "Failed", Icon: AlertTriangle },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.not_started;
  const Icon = s.Icon;
  return (
    <span data-testid={`mod-status-${status}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${s.cls}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

export default function MoveOutDay() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { queuedCount = 0, failedCount = 0, syncing = false, isOnline = true, retryFailed } = useSync();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const res = await api.get(`/properties/${propertyId}/moveout-day`);
      setData(res.data);
    } catch { setError(true); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  // Merge the live client capture queue into the Evidence sync section (truthful queue state).
  const queueState = useMemo(() => {
    if (failedCount > 0) return "failed";
    if (syncing) return "uploading";
    if (queuedCount > 0) return "pending";
    return null;
  }, [failedCount, syncing, queuedCount]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] grid place-items-center p-6" data-testid="mod-error">
        <div className="text-center">
          <p className="text-[#B91C1C]">Couldn't load Move-Out Day.</p>
          <button onClick={() => navigate(-1)} className="mt-3 text-sm font-medium text-brand hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col" data-testid="moveout-day-page">
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="mod-logo-link"><Logo /></Link>
          <button onClick={() => navigate(`/property/${propertyId}?tab=moveout`)} data-testid="mod-back-link"
            className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-brand">
            <ArrowLeft size={15} /> Property
          </button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {!data ? (
          <div className="space-y-4"><Skeleton className="h-10 w-2/3 rounded-lg" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-brand text-sm font-medium">
              <Camera size={16} /> Move-Out Day
            </div>
            <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-navy" data-testid="mod-title">
              Move-Out Day
            </h1>
            <p className="mt-1 text-sm text-[#475569]" data-testid="mod-address">{data.address}</p>

            {data.archived && (
              <div className="mt-4 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]" data-testid="mod-archived-note">
                This property is archived. Everything here is read-only.
              </div>
            )}

            <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-xs text-[#475569]" data-testid="mod-disclaimer">
              <Info size={15} className="mt-0.5 shrink-0 text-[#94A3B8]" />
              <span>{data.disclaimer}</span>
            </div>

            <div className="mt-6 space-y-5">
              {data.sections.map((section) => {
                const SIcon = SECTION_ICON[section.title] || Circle;
                return (
                  <section key={section.title} data-testid={`mod-section-${section.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
                    <div className="flex items-center gap-2">
                      <span className="grid place-items-center h-8 w-8 rounded-xl bg-brand/10 text-brand"><SIcon size={16} /></span>
                      <h2 className="font-heading text-lg font-semibold text-navy">{section.title}</h2>
                    </div>

                    {section.title === "Evidence sync" && queueState && (
                      <div className="mt-3 flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5" data-testid="mod-queue-state">
                        <div className="flex items-center gap-2 text-sm text-navy">
                          <StatusPill status={queueState} />
                          <span className="text-xs text-[#64748B]">
                            {queueState === "failed" ? `${failedCount} upload${failedCount !== 1 ? "s" : ""} failed`
                              : queueState === "uploading" ? "Uploading to TenantSeal…"
                              : `${queuedCount} waiting${!isOnline ? " (offline)" : ""}`}
                          </span>
                        </div>
                        {queueState === "failed" && !data.archived && retryFailed && (
                          <button data-testid="mod-retry-failed" onClick={() => retryFailed()} className="text-xs font-semibold text-brand hover:underline">Retry</button>
                        )}
                      </div>
                    )}

                    <ul className="mt-3 space-y-2">
                      {section.items.map((it) => (
                        <li key={it.key} data-testid={`mod-item-${it.key}`}
                          className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-navy truncate">{it.label}</span>
                              <StatusPill status={it.status} />
                            </div>
                            <p className="mt-0.5 text-xs text-[#64748B]">{it.detail}</p>
                          </div>
                          <button data-testid={`mod-open-${it.key}`} onClick={() => navigate(it.link)}
                            className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                            Open <ChevronRight size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
